import { api } from 'API'

export async function uploadFile(
  type: 'image' | 'video' | 'other',
  file: File,
  onUploadProgress?: (progressEvt: any) => void,
  // The /cms/{type} endpoints store under organizations/{organizationId}/ and the
  // BE requires organizationId as a mandatory query param. The request interceptor
  // only injects `orgId` (NOT organizationId), so callers MUST pass organizationId
  // here for org-scoped uploads (team picture, process media, etc.).
  organizationId?: number
): Promise<string> {
  // Hard guard: without a real org id the file would land under
  // `organizations/0/` (or be rejected 400) — fail loudly instead so the
  // caller's error handling surfaces it and no file is stored misfiled.
  if (!organizationId || organizationId <= 0) {
    throw new Error('Missing organization context — cannot upload file')
  }
  const body = new FormData()
  body.append('file', file, file.name)
  const response = await api.post(`/cms/${type}`, body, {
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    params: organizationId != null ? { organizationId } : undefined,
    onUploadProgress: onUploadProgress ?? (() => {})
  })

  // full download url
  return response.data
}

/**
 * Upload a profile avatar — USER-scoped, not org-scoped. The BE derives the userId
 * from the JWT and files it under users/{userId}/{profileId}/avatars/. Pass the
 * athlete/coach profileId being edited.
 */
export async function uploadAvatar(
  file: File,
  profileId: number,
  onUploadProgress?: (progressEvt: any) => void
): Promise<string> {
  const body = new FormData()
  body.append('file', file, file.name)
  const response = await api.post('/cms/profile-image', body, {
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    params: { profileId },
    onUploadProgress: onUploadProgress ?? (() => {})
  })

  // full download url
  return response.data
}

/**
 * Upload a profile MEDIA image — USER-scoped, filed under
 * users/{userId}/{profileId}/media/ (same security model as uploadAvatar, different
 * sub-folder). Returns the CDN url. One file per call; callers parallelise multi-select.
 */
export async function uploadProfileMedia(
  file: File,
  profileId: number,
  onUploadProgress?: (progressEvt: any) => void
): Promise<string> {
  const body = new FormData()
  body.append('file', file, file.name)
  const response = await api.post('/cms/profile-media', body, {
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    params: { profileId },
    onUploadProgress: onUploadProgress ?? (() => {})
  })

  // full download url
  return response.data
}

export async function uploadMultipleFiles(
  type: 'image' | 'video' | 'other',
  files: File[],
  onUploadProgress?: (progressEvt: any) => void
): Promise<string[]> {
  const body = new FormData()
  files.forEach(file => body.append('files', file, file.name))
  const response = await api.post(`/cms/${type}/multiple`, body, {
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    onUploadProgress: onUploadProgress ?? (() => {})
  })

  // full download url
  return response.data
}
